import React, { useCallback, useEffect, useState } from 'react';
import Seo from '../components/Seo';
import Bande from '../components/Bande';
import NoteEtoiles from '../components/NoteEtoiles';
import { encoder } from '../components/avisService';

// Page de gestion des avis, reservee au gerant.
// L'acces est protege par une cle secrete, verifiee par la fonction serveur.
// La page n'est pas indexee et n'est liee depuis aucune autre page du site.

const CLE_LOCALE = 'bstp-avis-cle';

const MOTIFS = [
  'Aucune location correspondante retrouvée',
  'Contenu injurieux ou menaçant',
  'Contenu diffamatoire ou mettant en cause une personne',
  'Contenu hors sujet ou publicitaire',
  'Avis manifestement faux',
  'Doublon',
];

function formaterMois(aaaaMm) {
  if (!aaaaMm) return 'non précisé';
  const [a, m] = String(aaaaMm).split('-');
  const d = new Date(Number(a), Number(m) - 1, 1);
  if (Number.isNaN(d.getTime())) return 'non précisé';
  return d.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
}

function formaterDate(iso) {
  try {
    return new Date(iso).toLocaleDateString('fr-FR', {
      day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch (e) {
    return '';
  }
}

const AFFICHAGES = {
  'nom-entreprise': 'Nom et entreprise',
  entreprise: 'Entreprise seule',
  prenom: "Prénom et initiale",
  anonyme: 'Anonyme',
};

const PASTILLES = {
  attente: 'bg-jaune text-nuit',
  publie: 'bg-[#1B7A4B] text-white',
  refuse: 'bg-beton text-white',
};

const LIBELLES = { attente: 'En attente', publie: 'Publié', refuse: 'Refusé' };

export default function GestionAvis() {
  const [cle, setCle] = useState('');
  const [saisie, setSaisie] = useState('');
  const [donnees, setDonnees] = useState(null);
  const [chargement, setChargement] = useState(false);
  const [erreur, setErreur] = useState('');
  const [filtre, setFiltre] = useState('attente');
  const [enCours, setEnCours] = useState('');

  const charger = useCallback(async (k) => {
    if (!k) return;
    setChargement(true);
    setErreur('');
    try {
      const r = await fetch(`/api/avis-gestion?cle=${encodeURIComponent(k)}`);
      const d = await r.json();
      if (!r.ok) {
        setErreur(d.erreur || 'Accès refusé.');
        setDonnees(null);
        if (r.status === 401) { setCle(''); localStorage.removeItem(CLE_LOCALE); }
      } else {
        setDonnees(d);
        localStorage.setItem(CLE_LOCALE, k);
        setCle(k);
      }
    } catch (e) {
      setErreur("La fonction de gestion ne répond pas. Vérifiez qu'elle est bien déployée.");
    }
    setChargement(false);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const k = params.get('cle') || localStorage.getItem(CLE_LOCALE) || '';
    if (k) charger(k);
  }, [charger]);

  async function decider(avis, decision) {
    let motif = '';
    if (decision === 'refuse') {
      // eslint-disable-next-line no-alert
      motif = window.prompt(
        `Motif du refus, communiqué à l'auteur si vous le contactez.\n\nSuggestions :\n- ${MOTIFS.join('\n- ')}`,
        MOTIFS[0],
      );
      if (motif === null) return;
    }
    setEnCours(avis.id);
    try {
      await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: encoder({ 'form-name': 'avis-decisions', ref: avis.id, decision, motif: motif || '' }),
      });
      // Netlify met une seconde ou deux a indexer l'envoi.
      setTimeout(() => charger(cle), 1500);
    } catch (e) {
      setErreur("La décision n'a pas pu être enregistrée. Réessayez.");
    }
    setEnCours('');
  }

  if (!cle || !donnees) {
    return (
      <>
        <Seo titre="Gestion des avis" description="Page réservée." noindex />
        <section className="bg-nuit text-white relative overflow-hidden min-h-[420px] flex items-center">
          <div className="absolute inset-0 trame-diagonale" aria-hidden="true" />
          <div className="conteneur relative py-16">
            <h1 className="font-display uppercase text-[32px] sm:text-[40px] leading-[1]">
              Gestion des <span className="mot-degrade">avis</span>
            </h1>
            <p className="mt-4 text-white/70 max-w-lg">
              Page réservée. Saisissez votre clé d'accès pour voir les avis reçus et décider
              de leur publication.
            </p>
            <form
              className="mt-7 flex flex-col sm:flex-row gap-3 max-w-xl"
              onSubmit={(e) => { e.preventDefault(); charger(saisie.trim()); }}
            >
              <input
                type="password" value={saisie} onChange={(e) => setSaisie(e.target.value)}
                placeholder="Clé d'accès" autoComplete="off"
                className="flex-1 border border-white/25 bg-white/10 rounded-[3px] px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-jaune"
              />
              <button type="submit" className="btn-jaune whitespace-nowrap" disabled={chargement}>
                {chargement ? 'Vérification…' : 'Entrer'}
              </button>
            </form>
            {erreur && <p className="mt-4 text-orange font-semibold">{erreur}</p>}
          </div>
          <Bande hauteur={6} />
        </section>
      </>
    );
  }

  const liste = donnees.avis.filter((a) => (filtre === 'tous' ? true : a.statut === filtre));

  return (
    <>
      <Seo titre="Gestion des avis" description="Page réservée." noindex />

      <section className="bg-nuit text-white relative overflow-hidden">
        <div className="absolute inset-0 trame-diagonale" aria-hidden="true" />
        <div className="conteneur relative py-10 sm:py-12">
          <h1 className="font-display uppercase text-[30px] sm:text-[38px] leading-[1]">
            Gestion des <span className="mot-degrade">avis</span>
          </h1>
          <div className="mt-5 flex flex-wrap gap-6">
            {[
              ['En attente', donnees.enAttente, 'text-jaune'],
              ['Publiés', donnees.publies, 'text-white'],
              ['Refusés', donnees.refuses, 'text-white/60'],
            ].map(([libelle, n, couleur]) => (
              <div key={libelle}>
                <p className={`font-display text-[30px] leading-none ${couleur}`}>{n}</p>
                <p className="font-cond uppercase tracking-[0.08em] text-[12.5px] text-white/60 mt-1">{libelle}</p>
              </div>
            ))}
          </div>
        </div>
        <Bande hauteur={6} />
      </section>

      <section className="bg-trame py-10 sm:py-14">
        <div className="conteneur">
          <div className="flex flex-wrap gap-2">
            {[['attente', 'En attente'], ['publie', 'Publiés'], ['refuse', 'Refusés'], ['tous', 'Tous']].map(([v, l]) => (
              <button
                key={v} type="button" onClick={() => setFiltre(v)}
                aria-pressed={filtre === v}
                className={`font-cond uppercase font-bold tracking-[0.06em] text-[14px] px-4 py-2 rounded-[3px] border-2 transition-colors ${
                  filtre === v ? 'bg-nuit text-white border-nuit' : 'border-nuit/20 text-nuit hover:border-jaune'
                }`}
              >
                {l}
              </button>
            ))}
            <button
              type="button" onClick={() => charger(cle)}
              className="ml-auto font-cond uppercase tracking-[0.06em] text-[14px] text-bleu hover:text-orange"
            >
              Rafraîchir
            </button>
          </div>

          {erreur && <p className="mt-4 text-orange font-semibold">{erreur}</p>}

          {liste.length === 0 ? (
            <p className="mt-8 carte p-6 text-nuit/75">Aucun avis dans cette catégorie.</p>
          ) : (
            <div className="mt-6 space-y-5">
              {liste.map((a) => (
                <article key={a.id} className="carte p-6">
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-3 flex-wrap">
                        <NoteEtoiles note={a.note} taille={17} classe="text-jaune" />
                        <span className={`font-cond uppercase tracking-[0.08em] text-[11.5px] px-2 py-0.5 rounded-[2px] ${PASTILLES[a.statut]}`}>
                          {LIBELLES[a.statut]}
                        </span>
                      </div>
                      <p className="mt-3 font-cond uppercase font-bold tracking-[0.03em] text-[16px] text-nuit">
                        {a.nomReel || 'Sans nom'}
                        {a.societeReelle ? <span className="text-bleu">, {a.societeReelle}</span> : null}
                      </p>
                      <p className="mt-1 text-[14px] text-beton">
                        {a.contact || 'pas de mail'} · Chantier de {formaterMois(a.chantier)}
                        {a.commune ? ` · ${a.commune}` : ''}
                        {a.machine ? ` · ${a.machine}` : ''}
                      </p>
                      <p className="mt-1 text-[13px] text-beton/80">
                        Reçu le {formaterDate(a.date)} · Affichage souhaité : {AFFICHAGES[a.affichage] || a.affichage}
                        {a.statut !== 'attente' && a.decideLe ? ` · Décidé le ${formaterDate(a.decideLe)}` : ''}
                      </p>
                    </div>

                    <div className="flex gap-2 shrink-0">
                      {a.statut !== 'publie' && (
                        <button
                          type="button" onClick={() => decider(a, 'publie')} disabled={enCours === a.id}
                          className="btn-jaune !py-2.5 !px-5 !text-[14px]"
                        >
                          Publier
                        </button>
                      )}
                      {a.statut !== 'refuse' && (
                        <button
                          type="button" onClick={() => decider(a, 'refuse')} disabled={enCours === a.id}
                          className="btn-ligne !py-2.5 !px-5 !text-[14px]"
                        >
                          Refuser
                        </button>
                      )}
                    </div>
                  </div>

                  <p className="mt-4 pt-4 border-t border-nuit/12 text-nuit/85 whitespace-pre-line">{a.texte}</p>

                  {a.motif && (
                    <p className="mt-3 text-[14px] text-beton">Motif enregistré : {a.motif}</p>
                  )}
                </article>
              ))}
            </div>
          )}

          <div className="mt-10 carte p-6 border-l-4 border-l-bleu">
            <h2 className="font-cond uppercase font-bold tracking-[0.04em] text-[17px] text-nuit">
              Ce que vous pouvez refuser, et ce que vous ne pouvez pas
            </h2>
            <p className="mt-2.5 text-nuit/75 text-[15.5px]">
              Vous pouvez refuser un avis injurieux, diffamatoire, hors sujet, manifestement faux,
              ou qui ne correspond à aucune location retrouvée. Vous ne pouvez pas refuser un avis
              négatif portant sur une prestation réellement effectuée : la loi l'interdit et la
              sanction va jusqu'à 300 000 €. Dans ce cas, publiez-le et répondez au client.
            </p>
          </div>
        </div>
      </section>
    </>
  );
}
